import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Table } from 'ui/Table';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { Multiselect } from 'ui/Multiselect';
import SingleDateInput from 'pureUi/SingleDateInput';
import { usePagination } from 'hooks/usePagination';
import { ESortOrder } from 'store/common/types';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { useImportLogQuery, IMPORT_LOG_QUERY_KEY, defaultImportLogFilters } from '../hooks/useImportLogQuery';
import { useImportDetailQuery } from '../hooks/useImportDetailQuery';
import {
  EBankTransactionImportStatus,
  IBankTransactionImportResponse,
  IImportLogFilters,
  TImportLogSortKey,
} from '../types';

/**
 * Format import date as "DD MMM YY, HH:mm" (e.g., "25 Jul 24, 01:15")
 */
const formatImportDate = (dateString: string): string => {
  const date = parseISO(dateString);
  return format(date, 'dd MMM yy, HH:mm');
};

/**
 * Get results count text
 */
const getResultsText = (count: number): string => {
  if (!count) return 'no results found';
  if (count === 1) return '1 result found';
  return `${count} results found`;
};

/**
 * Table column definitions
 */
const tableColumns: ITableColumn[] = [
  { id: 'date', label: 'DATE', width: '120px', fieldToSortBy: 'createdAt' },
  { id: 'file', label: 'FILE', width: '180px', fieldToSortBy: 'filename' },
  { id: 'user', label: 'USER', width: '180px', fieldToSortBy: 'createdBy' },
  { id: 'status', label: 'STATUS', width: '100px', fieldToSortBy: 'status' },
  {
    id: 'receipts',
    label: 'RECEIPTS',
    width: '80px',
    fieldToSortBy: 'totalRowsCount',
    className: 'text-right',
  },
  {
    id: 'errors',
    label: 'ERRORS',
    width: '80px',
    fieldToSortBy: 'rowErrorsCount',
    className: 'text-right',
  },
];

/**
 * Status filter dropdown options
 */
const statusOptions = [
  { value: '', label: 'All' },
  { value: EBankTransactionImportStatus.SUCCESS, label: 'Success' },
  { value: EBankTransactionImportStatus.FAILED, label: 'Failed' },
];

/**
 * Status badge component
 */
const ImportLogStatusBadge: React.FC<{ status: EBankTransactionImportStatus }> = ({ status }) => {
  if (status === EBankTransactionImportStatus.SUCCESS) {
    return <span className="text-status-confirmed font-pt-sans">Success</span>;
  }
  return <span className="text-status-cancelled font-pt-sans">Failed</span>;
};

/**
 * Expandable error details component
 */
const ImportLogErrorDetails: React.FC<{ importRecord: IBankTransactionImportResponse }> = ({ importRecord }) => {
  const rowErrors = importRecord.parseResult?.rowErrors || [];

  if (rowErrors.length === 0) {
    return null;
  }

  return (
    <div className="bg-white-hard border-gray-20 border border-t-0 px-[20px] py-[10px] pb-[20px] space-y-[10px]">
      {rowErrors.map(rowError => (
        <div key={rowError.rowNumber}>
          <span className="text-red-95 italic font-pt-sans">Row {rowError.rowNumber}</span>
          <div className="text-gray-100 ml-[10px] font-pt-sans">
            {rowError.cellErrors.map((cellError, i) => (
              <div key={i}>
                {cellError.header}: {cellError.message}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};


const ImportLogFilename: React.FC<{ importUuid: string; filename: string }> = ({ importUuid, filename }) => {
  const [shouldFetch, setShouldFetch] = useState(false);
  const { data, isLoading } = useImportDetailQuery(shouldFetch ? importUuid : null);
  const hasOpened = useRef(false);

  useEffect(() => {
    if (data?.url && !hasOpened.current) {
      hasOpened.current = true;
      window.open(data.url, '_blank');
      setShouldFetch(false);
    }
  }, [data?.url]);

  return (
    <div
      onClick={() => setShouldFetch(true)}
      className="text-brown-prime hover:underline font-pt-sans cursor-pointer"
    >
      {filename}
      {isLoading && <span className="ml-2 text-gray-100">(loading...)</span>}
    </div>
  );
};

export const ImportLogTab: React.FC = () => {
  const queryClient = useQueryClient();

  // Filter state (local, not URL)
  const [filters, setFilters] = useState<IImportLogFilters>(defaultImportLogFilters);

  // Pagination state (1-indexed)
  const paginationData = usePagination({ initialPage: 1, initialPerPage: 10, initialTotal: 0 });

  // Sorting state - default sort by date descending (most recent first)
  const [sortBy, setSortBy] = useState<TImportLogSortKey>('createdAt');
  const [sortOrder, setSortOrder] = useState<ESortOrder>(ESortOrder.DESC);

  // Fetch data with React Query
  const { data, isLoading, isError, isFetching } = useImportLogQuery(
    paginationData.page,
    paginationData.perPage,
    sortBy,
    sortOrder,
    filters
  );

  // Track if we've ever loaded data (stays true forever once set)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Update total when data loads, and mark as loaded once
  useEffect(() => {
    if (data?.meta.total !== undefined) {
      paginationData.setTotal(data.meta.total);
      if (!hasLoadedOnce) {
        setHasLoadedOnce(true);
      }
    }
  }, [data?.meta.total]);

  // Clean up queries on unmount
  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: [IMPORT_LOG_QUERY_KEY] });
    };
  }, [queryClient]);

  // Filter change handlers
  const handleStartDateChange = useCallback(
    (date: Date) => {
      setFilters(prev => ({
        ...prev,
        startDate: date ? format(date, 'yyyy-MM-dd') : null,
      }));
      paginationData.setPage(1);
    },
    [paginationData]
  );

  const handleEndDateChange = useCallback(
    (date: Date) => {
      setFilters(prev => ({
        ...prev,
        endDate: date ? format(date, 'yyyy-MM-dd') : null,
      }));
      paginationData.setPage(1);
    },
    [paginationData]
  );

  const handleStatusChange = useCallback(
    (values: string[]) => {
      const selectedValue = values[0];
      setFilters(prev => ({
        ...prev,
        statuses: selectedValue ? [selectedValue as EBankTransactionImportStatus] : [],
      }));
      paginationData.setPage(1);
    },
    [paginationData]
  );

  // Handle sort
  const handleSort = useCallback((newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy as TImportLogSortKey);
    setSortOrder(newSortOrder as ESortOrder);
  }, []);

  // Transform data to table rows
  const tableRows: ITableRow[] = useMemo(() => {
    const imports = data?.data || [];
    return imports.map((importRecord: IBankTransactionImportResponse) => {
      const errorCount = importRecord.parseResult?.rowErrors?.length ?? 0;
      const totalRows = importRecord.parseResult?.totalRows ?? 0;
      const successCount = totalRows - errorCount;
      const fullName = `${importRecord.createdBy.firstName} ${importRecord.createdBy.lastName}`;

      return {
        id: importRecord.uuid,
        cells: [
          { id: 'date', value: formatImportDate(importRecord.createdAt) },
          {
            id: 'file',
            value: <ImportLogFilename importUuid={importRecord.uuid} filename={importRecord.filename} />,
          },
          { id: 'user', value: <span className="font-pt-sans">{fullName}</span> },
          { id: 'status', value: <ImportLogStatusBadge status={importRecord.status} /> },
          {
            id: 'receipts',
            value: <span className="font-pt-sans">{successCount}</span>,
            className: 'text-center',
          },
          {
            id: 'errors',
            value: <span className="font-pt-sans">{errorCount}</span>,
            className: 'text-center',
          },
        ],
        // Only show expandable content if there are errors
        expandableContent: errorCount > 0 ? <ImportLogErrorDetails importRecord={importRecord} /> : undefined,
      };
    });
  }, [data?.data]);

  const total = data?.meta.total || 0;

  // Get selected status value for dropdown
  const selectedStatusValue = filters.statuses.length > 0 ? filters.statuses[0] : '';

  return (
    <div className="import-log-tab w-full">
      {/* Page Title with Count */}
      <div className="mb-4">
        <span className="font-noe-display text-21px">
          Import Log{' '}
          {hasLoadedOnce && <span className="font-pt-sans text-15px text-gray-100">({getResultsText(total)})</span>}
          {isFetching && hasLoadedOnce && (
            <span className="font-pt-sans text-15px text-gray-100 ml-2">Loading ...</span>
          )}
        </span>
      </div>

      {/* Error state */}
      {isError && <ErrorBar />}

      {/* Initial loading state - only shown before first successful load */}
      {!isError && !hasLoadedOnce && <LoadingBar />}

      {/* Filters and Table - show after first load, stays visible during all refetches */}
      {!isError && hasLoadedOnce && (
        <>
          {/* Filter Row */}
          <div className="flex flex-wrap gap-5 items-end mb-5">
            {/* Start Date */}
            <div className="flex flex-col">
              <span className="font-pt-sans text-[13px] text-black mb-[5px]">Start Date</span>
              <SingleDateInput
                buttonClassName="!normal-case !h-[35px] !bg-ivory !border-gray-40"
                displayStringClassName="!font-pt-sans"
                value={filters.startDate ? parseISO(filters.startDate) : null}
                onChange={handleStartDateChange}
                enablePastDates={true}
                placeholder="Select date..."
                placeholderClassName="italic text-gray-100"
                calendarCount={1}
              />
            </div>

            {/* End Date */}
            <div className="flex flex-col">
              <span className="font-pt-sans text-[13px] text-black mb-[5px]">End Date</span>
              <SingleDateInput
                buttonClassName="!normal-case !h-[35px] !bg-ivory !border-gray-40"
                displayStringClassName="!font-pt-sans"
                value={filters.endDate ? parseISO(filters.endDate) : null}
                onChange={handleEndDateChange}
                enablePastDates={true}
                placeholder="Select date..."
                placeholderClassName="italic text-gray-100"
                calendarCount={1}
              />
            </div>

            {/* Status */}
            <div className="flex flex-col">
              <span className="font-pt-sans text-[13px] text-black mb-[5px]">Status</span>
              <Multiselect
                className="bg-ivory min-w-[120px]"
                itemsClassname="bg-ivory"
                itemCtaClassName="hover:bg-gray-10"
                options={statusOptions}
                selectedValues={[selectedStatusValue]}
                onUpdate={handleStatusChange}
                isSingleSelectMode={true}
                isCloseOnSelect={true}
                hideCheckboxes={true}
                placeholderText="All"
              />
            </div>
          </div>

          {/* Table */}
          <Table
            columns={tableColumns}
            rows={tableRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={isFetching}
            messageWhenEmpty="No Results"
            className="mt-5"
            evenColor="white-hard"
          />

          {/* Pagination */}
          {tableRows.length >= 1 && (
            <Pagination
              currentPage={paginationData.page}
              itemsPerPage={paginationData.perPage}
              onItemsPerPageChange={paginationData.setPerPage}
              onPageSelect={paginationData.setPage}
              pageCount={paginationData.pageCount}
            />
          )}
        </>
      )}
    </div>
  );
};
